import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SEARCH_CONFIG } from './itemSearch';
import stableStringify from 'fast-json-stable-stringify';

// TItem represents the type of a single item in the paginated list
export interface PaginatedCacheData<TItem> {
  items: TItem[]; // All items loaded so far for this search
  hasNextPage: boolean;
  nextPageToFetch: number; // The page number for the next fetch (1-indexed)
}

// New interface for what transformResponse returns
export interface TransformResponseResult<TItem> {
  items: TItem[];
  hasNextPage: boolean;
}

// Internal structure stored in the cache, includes timestamp for TTL
interface InternalCacheEntry<TItem> extends PaginatedCacheData<TItem> {
  timestamp: number;
}

interface UseItemSearchCacheOptions {
  cacheTimeout?: number; // milliseconds
  maxCacheSize?: number;
}

/**
 * Hook for caching item search results (with page-based pagination support)
 * to improve performance and reduce redundant API calls.
 * TItem is the type of a single item in the list.
 */
export function useItemSearchCache<TItem>(
  options: UseItemSearchCacheOptions = {}
) {
  const { cacheTimeout = 5 * 60 * 1000, maxCacheSize = 50 } = options; // 5 minutes default cache
  
  // Replace cacheRef with a structure that includes a doubly-linked list for efficient eviction
  const cacheRef = useRef<{ 
    map: Map<string, InternalCacheEntry<TItem>>;
    evictionList: { key: string; prev: string | null; next: string | null }[];
  }>({
    map: new Map(),
    evictionList: [],
  });
  const [forceUpdate, setForceUpdate] = useState(0); // Used to update cacheStats

  const triggerUpdate = useCallback(() => {
    setForceUpdate(prev => prev + 1);
  }, []);

  const getCacheKey = useCallback((searchTerm: string, filters: Record<string, any> = {}) => {
    // Consider more robust serialization for filters if they can have varying key orders
    return `${searchTerm}:${stableStringify(filters)}`;
  }, []);

  const isValidCacheEntry = useCallback((entry: InternalCacheEntry<TItem>) => {
    return Date.now() - entry.timestamp < cacheTimeout;
  }, [cacheTimeout]);

  const get = useCallback((searchTerm: string, filters: Record<string, any> = {}): PaginatedCacheData<TItem> | null => {
    const key = getCacheKey(searchTerm, filters);
    const entry = cacheRef.current.map.get(key);
    
    if (entry && isValidCacheEntry(entry)) {
      // Return data without the internal timestamp
      const { timestamp, ...dataToReturn } = entry;
      return dataToReturn;
    }
    
    // Clean up expired entry if found
    if (entry) {
      cacheRef.current.map.delete(key);
      cacheRef.current.evictionList = cacheRef.current.evictionList.filter(item => item.key !== key);
      triggerUpdate(); // Cache size might have changed
    }
    
    return null;
  }, [getCacheKey, isValidCacheEntry, triggerUpdate]);

  const set = useCallback((
    searchTerm: string,
    filters: Record<string, any> = {},
    data: PaginatedCacheData<TItem> // This now includes all items, hasNextPage, and nextPageToFetch
  ) => {
    const key = getCacheKey(searchTerm, filters);
    
    const newEntry: InternalCacheEntry<TItem> = {
      ...data,
      timestamp: Date.now(),
    };
    
    if (cacheRef.current.map.size >= maxCacheSize && !cacheRef.current.map.has(key)) {
      // Evict the oldest entry using the eviction list
      const oldest = cacheRef.current.evictionList.shift();
      if (oldest) {
        cacheRef.current.map.delete(oldest.key);
      }
    }

    // Add the new entry to the map and eviction list
    cacheRef.current.map.set(key, newEntry);
    cacheRef.current.evictionList.push({ key, prev: null, next: null });

    triggerUpdate(); // To update cacheStats
  }, [getCacheKey, maxCacheSize, triggerUpdate]);

  const clear = useCallback(() => {
    cacheRef.current.map.clear();
    cacheRef.current.evictionList = [];
    triggerUpdate();
  }, [triggerUpdate]);

  const clearExpired = useCallback(() => {
    const now = Date.now();
    const keysToDelete: string[] = [];

    cacheRef.current.map.forEach((entry, key) => {
      if (now - entry.timestamp >= cacheTimeout) {
        keysToDelete.push(key);
      }
    });

    if (keysToDelete.length > 0) {
      keysToDelete.forEach(key => {
        cacheRef.current.map.delete(key);
        cacheRef.current.evictionList = cacheRef.current.evictionList.filter(item => item.key !== key);
      });
      triggerUpdate();
    }
  }, [cacheTimeout, triggerUpdate]);

  // Clean up expired entries periodically
  useEffect(() => {
    const interval = setInterval(clearExpired, cacheTimeout / 2);
    return () => clearInterval(interval);
  }, [clearExpired, cacheTimeout]);

  const cacheStats = useMemo(() => ({
    size: cacheRef.current.map.size,
    maxSize: maxCacheSize,
  }), [maxCacheSize, forceUpdate]); // forceUpdate ensures stats are fresh when cache changes

  return useMemo(() => ({
    get,
    set,
    clear,
    cacheStats,
  }), [get, set, clear, cacheStats]);
}

/**
 * Hook for optimizing item dropdown performance with debouncing,
 * caching, and page-based "load more" pagination.
 * TItem is the type of a single item in the list.
 * TQueryVariables is the type for the variables of the query function.
 * TQueryResult is the raw result type from the query function.
 */
export function useOptimizedItemSearch<
  TItem,
  TQueryVariables extends Record<string, any>,
  TQueryResult,
>(
  initialSearchTerm: string = '',
  initialFilters: Record<string, any> = {}, // These are the base filters, page number is managed internally
  options: {
    minSearchLength?: number;
    debounceDelay?: number;
    cacheTimeout?: number;
    allowEmptySearch?: boolean;
    itemsPerPage?: number; // Number of items to fetch per page
    executeQuery: (variables: TQueryVariables) => Promise<TQueryResult>;
    // transformResponse now needs to know the items per page to infer hasNextPage
    transformResponse: (response: TQueryResult, itemsPerPage: number) => TransformResponseResult<TItem>;
    getVariables: (
      searchTerm: string,
      filters: Record<string, any>,
      page: number, // Page number (1-indexed)
      limit: number
    ) => TQueryVariables;
  },
) {
  const {
    minSearchLength = SEARCH_CONFIG.MIN_SEARCH_LENGTH,
    debounceDelay = SEARCH_CONFIG.DEBOUNCE_DELAY,
    cacheTimeout, // Passed to useItemSearchCache
    allowEmptySearch = false,
    itemsPerPage = 20, // Default items per page
    executeQuery,
    transformResponse,
    getVariables,
  } = options;

  const effectiveMinSearchLength = allowEmptySearch ? 0 : minSearchLength;

  const [searchTermInput, setSearchTermInput] = useState<string>(initialSearchTerm);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>(initialSearchTerm);
  
  // activeSearchTerm and activeFilters represent the parameters for the *current* set of displayed items
  const [activeSearchTerm, setActiveSearchTerm] = useState<string>(initialSearchTerm);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>(initialFilters);

  const [displayedItems, setDisplayedItems] = useState<TItem[]>([]);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  // nextPageToFetch is 1-indexed for the page number to request in the API
  const [nextPageToFetch, setNextPageToFetch] = useState<number>(1); 
  
  const [isLoadingInitial, setIsLoadingInitial] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [queryError, setQueryError] = useState<Error | null>(null);

  const cache = useItemSearchCache<TItem>({ cacheTimeout });

  // Debounce search term input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTermInput);
    }, debounceDelay);
    return () => clearTimeout(handler);
  }, [searchTermInput, debounceDelay]);

  const shouldMakeInitialSearch = useMemo(() => {
    return debouncedSearchTerm.length >= effectiveMinSearchLength;
  }, [debouncedSearchTerm, effectiveMinSearchLength]);

  // Effect for fetching initial data (page 1)
  useEffect(() => {
    if (!shouldMakeInitialSearch) {
      setDisplayedItems([]);
      setHasNextPage(false);
      setNextPageToFetch(1);
      setIsLoadingInitial(false);
      return;
    }

    // New search term or filters trigger a reset
    setActiveSearchTerm(debouncedSearchTerm);
    setActiveFilters(initialFilters); // Use the stable initialFilters from props
    setDisplayedItems([]);
    setNextPageToFetch(1); // Reset to page 1 for the operation
    setHasNextPage(false); // Assume no next page until fetch completes
    setIsLoadingInitial(true);
    setQueryError(null);

    const fetchFirstPage = async () => {
      const cacheKeySearchTerm = debouncedSearchTerm;
      const cacheKeyFilters = initialFilters;
      const cachedData = cache.get(cacheKeySearchTerm, cacheKeyFilters);

      if (cachedData) {
        setDisplayedItems(cachedData.items);
        setHasNextPage(cachedData.hasNextPage);
        setNextPageToFetch(cachedData.nextPageToFetch);
        setIsLoadingInitial(false);
        return;
      }

      try {
        const pageToFetch = 1; // Explicitly fetching page 1
        const variables = getVariables(cacheKeySearchTerm, cacheKeyFilters, pageToFetch, itemsPerPage);
        const response = await executeQuery(variables);
        const transformedResult = transformResponse(response, itemsPerPage); // Gets { items, hasNextPage }
        // Use hasNextPage from transformedResult (which should use pageInfo if available)
        const newNextPageToFetch = transformedResult.hasNextPage ? pageToFetch + 1 : pageToFetch;

        setDisplayedItems(transformedResult.items);
        setHasNextPage(transformedResult.hasNextPage);
        setNextPageToFetch(newNextPageToFetch); // Set calculated next page

        cache.set(cacheKeySearchTerm, cacheKeyFilters, {
          items: transformedResult.items,
          hasNextPage: transformedResult.hasNextPage,
          nextPageToFetch: newNextPageToFetch, // Cache calculated next page
        });
      } catch (err) {
        setQueryError(err as Error);
        setDisplayedItems([]);
        setHasNextPage(false);
        // nextPageToFetch remains 1 (or its current value before error), which is fine.
      } finally {
        setIsLoadingInitial(false);
      }
    };

    fetchFirstPage();
  }, [
    debouncedSearchTerm, 
    initialFilters, 
    shouldMakeInitialSearch, 
    cache, 
    executeQuery, 
    getVariables, 
    transformResponse,
    itemsPerPage
  ]);

  const loadMore = useCallback(async () => {
    if (!hasNextPage || isLoadingMore || isLoadingInitial) {
      return;
    }

    setIsLoadingMore(true);
    setQueryError(null);

    // nextPageToFetch state already holds the correct 1-indexed page number for the *next* fetch.
    const currentPageToFetch = nextPageToFetch; 

    try {
      const variables = getVariables(activeSearchTerm, activeFilters, currentPageToFetch, itemsPerPage);
      const response = await executeQuery(variables);
      const newPageTransformedResult = transformResponse(response, itemsPerPage); // Gets { items, hasNextPage }
      // Use hasNextPage from newPageTransformedResult (which should use pageInfo if available)
      const combinedItems = [...displayedItems, ...newPageTransformedResult.items];
      const newNextPageToFetchForStateAndCache = newPageTransformedResult.hasNextPage 
        ? currentPageToFetch + 1 
        : currentPageToFetch;

      setDisplayedItems(combinedItems);
      setHasNextPage(newPageTransformedResult.hasNextPage);
      setNextPageToFetch(newNextPageToFetchForStateAndCache); // Set calculated next page
      
      cache.set(activeSearchTerm, activeFilters, {
        items: combinedItems,
        hasNextPage: newPageTransformedResult.hasNextPage,
        nextPageToFetch: newNextPageToFetchForStateAndCache, // Cache calculated next page
      });

    } catch (err) {
      setQueryError(err as Error);
      // Potentially set hasNextPage to false or handle error state
      // nextPageToFetch remains as it was (the page we attempted to fetch)
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    hasNextPage, 
    isLoadingMore, 
    isLoadingInitial, 
    activeSearchTerm, 
    activeFilters, 
    nextPageToFetch, // This is the current page we intend to fetch
    cache, 
    executeQuery, 
    getVariables, 
    transformResponse,
    itemsPerPage,
    displayedItems
  ]);
  
  return {
    displayedItems,
    isLoadingInitial,
    isLoadingMore,
    hasNextPage,
    error: queryError,
    setSearchTerm: setSearchTermInput,
    loadMore,
    cacheStats: cache.cacheStats,
    clearCache: cache.clear,
  };
}
